/**
 * Work-order domain model.
 *
 * Shapes follow the platform's `work_order` table and routers (`ops.py`,
 * `work.py`), not the airfield application's sample: priority is 1–5, the
 * human identifier is `number`, `kind` is corrective/preventive/predictive/
 * project/inspection/out_of_scope, and position is a WGS-84 point the platform
 * computes. Out of scope is work the maintenance contract does not cover, kept
 * out of the corrective figures.
 *
 * The lifecycle is the platform's, and the platform enforces it — this module
 * exists so the UI can *offer* only legal moves and explain the rest. The API
 * remains the authority: a transition the UI thinks is legal may still be
 * refused (missing "after" photograph, segregation of duties) and the refusal
 * reason is shown as returned.
 *
 *   draft → approved → dispatched → in_progress → completed → verified
 *                          │            │
 *                          └─→ on_hold ─┘            (any) → cancelled
 */

export const WORK_ORDER_STATUSES = [
  "draft",
  "approved",
  "dispatched",
  "in_progress",
  "on_hold",
  "completed",
  "verified",
  "cancelled",
] as const;

export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

/** 1 = safety critical … 5 = routine. Matches `work_order.priority`. */
export const WORK_ORDER_PRIORITIES = [1, 2, 3, 4, 5] as const;
export type WorkOrderPriority = (typeof WORK_ORDER_PRIORITIES)[number];

export function priorityLabel(p: WorkOrderPriority): string {
  return `P${p}`;
}

export const PRIORITY_TITLE: Record<WorkOrderPriority, string> = {
  1: "P1 — safety critical, same day",
  2: "P2 — operational, within a week",
  3: "P3 — planned, within a month",
  4: "P4 — routine, within a quarter",
  5: "P5 — deferred, next programme",
};

export function toPriority(n: number | null | undefined): WorkOrderPriority {
  const v = Math.round(n ?? 3);
  return (v < 1 ? 1 : v > 5 ? 5 : v) as WorkOrderPriority;
}

export const WORK_ORDER_KINDS = ["corrective", "preventive", "predictive", "project", "inspection", "out_of_scope"] as const;
export type WorkOrderKind = (typeof WORK_ORDER_KINDS)[number];

export const KIND_LABEL: Record<WorkOrderKind, string> = {
  corrective: "Corrective",
  preventive: "Preventive",
  predictive: "Predictive",
  project: "Project",
  inspection: "Inspection",
  out_of_scope: "Out of scope",
};

export interface GeoPoint {
  lon: number;
  lat: number;
}

export interface WorkOrder {
  /** Platform id (uuid). */
  id: string;
  /** Human identifier, e.g. WO-0007. */
  number: string;
  siteId: string;
  title: string;
  kind: WorkOrderKind;
  priority: WorkOrderPriority;
  status: WorkOrderStatus;
  description?: string;
  zoneCode?: string;
  assetTag?: string;
  crew?: string;
  /** A single point to draw the job at, computed by the platform. */
  point?: GeoPoint;
  openedAt: string;
  dueAt?: string;
  startedAt?: string;
  completedAt?: string;
  verifiedAt?: string;
  costActual?: number;
  costPlanned?: number;
  labourHours?: number;
  slaHours?: number;
  /** The asset type the order was raised against (through its defect or alert), when there is one. */
  assetType?: string;
  assetTypeName?: string;
  /** The defect type it closes, when there is one. */
  defectTypeName?: string;
  /** The asset's discipline, else the zone's — how the platform already groups maintenance cost. */
  discipline?: string;
  /** The rule that raised it, when a rule did. */
  ruleKey?: string;
  capex?: boolean;
  /** Set when the order was raised from an alert. */
  alertId?: string;
  /** The defects this order closes (work_order_defect), when the platform lists them. */
  defectIds?: string[];
  /** The operator's own record id, when the order was loaded from elsewhere. */
  clientId?: string;
  /** When the contractor or crew confirmed they have the order (first confirmation), how, and who. */
  receivedAt?: string;
  receivedVia?: ReceiptVia;
  receivedBy?: string;
  /** Who closed it when that was a contractor with no account, through the job link. */
  completedByContact?: string;
  /** "person" — someone pressed Verify; "photo" — verified by itself because an after photo is on file. */
  verifiedVia?: "person" | "photo";
  /** The dispatch messages for this order, counted by outbox status (queued, sent, delivered, read, …). */
  delivery?: Record<string, number>;
  attrs?: Record<string, unknown>;
  /** Detail-only: people. */
  requestedBy?: string;
  completedBy?: string;
  verifiedBy?: string;
  /** Detail-only: the breakdown, the defects it closes, the photographs. */
  tasks?: WorkOrderTask[];
  defects?: WorkOrderDefect[];
  evidence?: WorkOrderEvidence[];
  /** Legal next states as the platform reported them, if it did. */
  allowedTransitions?: WorkOrderStatus[];
}

export interface WorkOrderTask {
  seq: number;
  description: string;
  status: string;
  labourHours?: number;
  completedAt?: string;
  note?: string;
}

export interface WorkOrderDefect {
  id: string;
  summary: string;
  severity: number;
  status: string;
  defectType?: string;
}

export interface WorkOrderEvidence {
  id: string;
  phase?: string;
  createdAt: string;
  objectId?: string;
  contentType?: string;
}

export interface WorkOrderEvent {
  id: string;
  workOrderId: string;
  at: string;
  by: string;
  from: WorkOrderStatus | null;
  to: WorkOrderStatus;
  note?: string;
  /** "note" — a note someone wrote, not a status change. */
  kind?: "note";
}

/** Mirrors the platform's `WorkOrderIn`. */
export interface CreateWorkOrderInput {
  siteId: string;
  title: string;
  kind: WorkOrderKind;
  priority: WorkOrderPriority;
  description?: string;
  zoneCode?: string;
  assetTag?: string;
  defectIds?: string[];
  slaHours?: number;
  capex?: boolean;
  /** The alert this work answers; the platform stores it and the explain panel lists the work. */
  alertId?: string;
  /** Where the work is, its trade, and the contractor (blank = own team) — see workOrderClass.ts. */
  area?: string;
  trade?: string;
  contractor?: string;
  /**
   * Minted once per record by the caller and reused on a retry, so sending
   * the same order twice files it once (the platform's client_id and
   * Idempotency-Key). Left out, a fresh one is minted per call.
   */
  clientId?: string;
}

/** How a receipt arrived — the platform's `RECEIPT_VIA`. */
export const RECEIPT_VIAS = ["app", "link", "phone", "email", "in_person", "other"] as const;
export type ReceiptVia = (typeof RECEIPT_VIAS)[number];

/** Mirrors the platform's `ReceiptIn`: the office records that the contractor has the order. */
export interface ReceiptInput {
  via: ReceiptVia;
  by?: string;
  note?: string;
}

/** How work orders behave at a site — the platform's `work_settings`. */
export interface WorkSettings {
  /** An after photograph on a completed order verifies it by itself. On unless the site turns it off. */
  autoVerifyPhoto: boolean;
}

/** What a contractor can do from the job link in the dispatch message. */
export type ContractorAction = "received" | "start" | "done";

/** Mirrors the platform's `ClassifyIn`: a blank one is cleared. */
export interface ClassifyInput {
  area?: string;
  trade?: string;
  contractor?: string;
}

/** Mirrors the platform's `SlaIn`: hours from opening, and why. */
export interface SetSlaInput {
  slaHours: number;
  reason: string;
}

/** Mirrors the platform's `TransitionIn`. */
export interface TransitionInput {
  to: WorkOrderStatus;
  note?: string;
  labourHours?: number;
  costActual?: number;
  onHoldReason?: string;
  allowMissingEvidence?: boolean;
}

/*
 * The platform's table, word for word (backend work/workorders.py
 * `TRANSITIONS`). The sample enforces it, so the demo refuses what the
 * platform refuses: until build 3 it let a completed order be cancelled
 * (the platform refuses that) and refused the platform's reopen
 * (completed → in progress) and its approved ↔ on hold moves.
 */
const TRANSITIONS: Record<WorkOrderStatus, readonly WorkOrderStatus[]> = {
  draft: ["approved", "cancelled"],
  approved: ["dispatched", "on_hold", "cancelled"],
  dispatched: ["in_progress", "on_hold", "cancelled"],
  in_progress: ["completed", "on_hold", "cancelled"],
  on_hold: ["approved", "dispatched", "in_progress", "cancelled"],
  completed: ["verified", "in_progress"],
  verified: [],
  cancelled: [],
};

export const TERMINAL_STATUSES: readonly WorkOrderStatus[] = ["verified", "cancelled"];
export const OPEN_STATUSES: readonly WorkOrderStatus[] = ["draft", "approved", "dispatched", "in_progress", "on_hold"];

export function legalTransitions(from: WorkOrderStatus): readonly WorkOrderStatus[] {
  return TRANSITIONS[from];
}

export function canTransition(from: WorkOrderStatus, to: WorkOrderStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

export function isOpen(status: WorkOrderStatus): boolean {
  return OPEN_STATUSES.includes(status);
}

export function isTerminal(status: WorkOrderStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

export const STATUS_LABEL: Record<WorkOrderStatus, string> = {
  draft: "Draft",
  approved: "Approved",
  dispatched: "Dispatched",
  in_progress: "In progress",
  on_hold: "On hold",
  completed: "Completed",
  verified: "Verified",
  cancelled: "Canceled",
};

/** Verb shown on the button that performs a transition. */
export const TRANSITION_VERB: Record<WorkOrderStatus, string> = {
  draft: "Return to draft",
  approved: "Approve",
  dispatched: "Dispatch",
  in_progress: "Start work",
  on_hold: "Put on hold",
  completed: "Complete",
  verified: "Verify",
  cancelled: "Cancel",
};

/** The close of the order for age purposes: completion, or cancellation. */
export function closedAt(wo: WorkOrder): string | undefined {
  return wo.completedAt ?? (wo.status === "cancelled" ? wo.verifiedAt : undefined);
}

/** Age in whole days between opening and now (or closing). */
export function ageDays(wo: WorkOrder, now: Date = new Date()): number {
  const closed = closedAt(wo);
  const end = closed ? new Date(closed) : now;
  const ms = end.getTime() - new Date(wo.openedAt).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

/** Overdue means past `due_at` when the platform set one; otherwise past the priority's allowance. */
export const OVERDUE_DAYS: Record<WorkOrderPriority, number> = {
  1: 1,
  2: 7,
  3: 30,
  4: 90,
  5: 180,
};

export function isOverdue(wo: WorkOrder, now: Date = new Date()): boolean {
  if (!isOpen(wo.status)) return false;
  if (wo.dueAt) return new Date(wo.dueAt).getTime() < now.getTime();
  return ageDays(wo, now) > OVERDUE_DAYS[wo.priority];
}

export interface WorkOrderSummary {
  total: number;
  open: number;
  byStatus: Record<WorkOrderStatus, number>;
  byPriority: Record<WorkOrderPriority, number>;
  p1Open: number;
  overdue: number;
}

export function summarise(orders: readonly WorkOrder[], now: Date = new Date()): WorkOrderSummary {
  const byStatus = Object.fromEntries(WORK_ORDER_STATUSES.map((s) => [s, 0])) as Record<WorkOrderStatus, number>;
  const byPriority = Object.fromEntries(WORK_ORDER_PRIORITIES.map((p) => [p, 0])) as Record<WorkOrderPriority, number>;
  let open = 0;
  let p1Open = 0;
  let overdue = 0;
  for (const wo of orders) {
    byStatus[wo.status] += 1;
    byPriority[wo.priority] += 1;
    if (isOpen(wo.status)) {
      open += 1;
      if (wo.priority === 1) p1Open += 1;
      if (isOverdue(wo, now)) overdue += 1;
    }
  }
  return { total: orders.length, open, byStatus, byPriority, p1Open, overdue };
}
